import { useMutation } from '@apollo/client';

import { SearchMode } from '@xing-com/crate-common-graphql-types';
import type { InteractionTrackingEvent } from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';
import type {
  CreateSearchAlertByQueryMutation,
  CreateSearchAlertByQueryMutationVariables,
} from '@xing-com/crate-jobs-graphql';
import { CreateSearchAlertByQueryDocument } from '@xing-com/crate-jobs-graphql';
import { useViewerData } from '@xing-com/hub';

import { addAlertByQueryToCachedList } from './apollo-helpers';
import type {
  PropTrackActionData,
  SearchAlertHookParams,
  UseCreateSearchAlertByQuery,
} from './types';
import { useShowSearchAlertErrorBanner } from './use-show-search-alert-error-banner';

const createOptimisticUpdate = (
  variables: CreateSearchAlertByQueryMutationVariables
): CreateSearchAlertByQueryMutation => ({
  __typename: 'Mutation',
  createSearchAlertByQuery: {
    __typename: 'SearchAlertCreationSuccess',
    alert: {
      __typename: 'SearchAlert',
      id: 'optimistic-response-id',
      globalId: 'optimistic-response-global-id',
      queryId: 'optimistic-response-query-id',
      query: { body: variables, searchMode: SearchMode.Normal },
      name: 'optimistic-response-name',
      newJobCount: null,
      hasNewJobs: null,
      createdAt: new Date().toString(),
    },
  },
});

export const useCreateSearchAlertByQuery = (
  params?: SearchAlertHookParams
): UseCreateSearchAlertByQuery => {
  const onCreateError = useShowSearchAlertErrorBanner(
    'CREATE',
    params?.nwtPage
  );
  const onDuplicatedError = useShowSearchAlertErrorBanner(
    'DUPLICATED',
    params?.nwtPage
  );

  const DEFAULT_PARAMS = {
    disableOptimisticUpdate: false,
    onError: (type?: string): void => {
      if (type === 'DUPLICATED') {
        onDuplicatedError();
      } else {
        onCreateError();
      }
    },
  };

  const { disableOptimisticUpdate, onError } = {
    ...DEFAULT_PARAMS,
    ...(params ?? {}),
  };

  const [createSearchAlertByQueryX1] = useMutation(
    CreateSearchAlertByQueryDocument
  );
  const userId = useViewerData().data?.user?.id;
  const { track: trackAdobeEvent } =
    useTracking<InteractionTrackingEvent<PropTrackActionData>>();

  return ({ variables, propTrackActionData, onCompleted }) => {
    if (propTrackActionData) {
      trackAdobeEvent({
        type: 'interaction',
        event: 'PropTrackAction',
        ...propTrackActionData,
      });
    }

    if (variables) {
      const { keywords, location, filter } = variables;
      createSearchAlertByQueryX1({
        variables: { keywords, location, filter },
        update: addAlertByQueryToCachedList(userId),
        optimisticResponse: disableOptimisticUpdate
          ? undefined
          : createOptimisticUpdate(variables),
        onCompleted: (response) => {
          if (
            response.createSearchAlertByQuery?.__typename ===
            'SearchAlertErrorResponse'
          ) {
            const { code } = response.createSearchAlertByQuery ?? {};
            onError(code === 'ALERT_EXISTS_ALREADY' ? 'DUPLICATED' : 'CREATE');
          }

          onCompleted?.(response);
        },
        onError: () => onError(),
      });
    }
  };
};
